<template>
  <TopNav :progress="progress" active-page="contact" @request-reset="resetProgressToZero" />
  <section class="scroll-track" ref="trackRef">
    <ContactEffect :progress="progress">
      <ContactContentYellow />
    </ContactEffect>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import TopNav from '@/components/TopNav.vue'
import ContactEffect from '@/components/ContactEffect.vue'
import ContactContentYellow from '@/components/ContactContentYellow.vue'

const progress = ref(0)
const trackRef = ref(null)
const scrollLocked = ref(true)

const clamp01 = (value) => Math.max(0, Math.min(1, value))

const getTrackStart = () => {
  if (!trackRef.value) return 0
  return trackRef.value.offsetTop
}

const forceTrackTop = () => {
  const top = getTrackStart()
  if (Math.abs(window.scrollY - top) > 1) {
    window.scrollTo({ top, behavior: 'auto' })
  }
}

const setScrollLock = (locked) => {
  scrollLocked.value = locked
  document.body.style.overflow = locked ? 'hidden' : ''
  if (locked) forceTrackTop()
}

const applyProgress = (next) => {
  progress.value = clamp01(next)
  setScrollLock(progress.value < 1)
}

const isAtTop = () => window.scrollY <= getTrackStart() + 1

// ─── Auto-play ────────────────────────────────────────
let autoRafId = 0
const cancelAuto = () => {
  if (autoRafId) {
    cancelAnimationFrame(autoRafId)
    autoRafId = 0
  }
}

const startAutoPlay = () => {
  const DURATION = 1600
  const start = performance.now()

  const tick = (now) => {
    const t = Math.min(1, (now - start) / DURATION)
    const eased = 1 - Math.pow(1 - t, 3)
    applyProgress(eased)
    if (t < 1) {
      autoRafId = requestAnimationFrame(tick)
    } else {
      autoRafId = 0
    }
  }

  autoRafId = requestAnimationFrame(tick)
}

// ─── Interação manual cancela o auto-play ─────────────
const handleWheel = (e) => {
  cancelAuto()
  const delta = e.deltaY * 0.001

  if (scrollLocked.value) {
    forceTrackTop()
    e.preventDefault()
    applyProgress(progress.value + delta)
    return
  }

  if (delta <= 0 && isAtTop()) {
    forceTrackTop()
    e.preventDefault()
    applyProgress(progress.value + delta)
  }
}

let touchStartY = 0
const handleTouchStart = (e) => {
  cancelAuto()
  touchStartY = e.touches[0]?.clientY ?? 0
}

const isInNavScroll = (target) => {
  const el = target instanceof Element ? target : null
  return !!el?.closest('.nav-scroll')
}

const handleTouchMove = (e) => {
  const currentY = e.touches[0]?.clientY ?? 0
  const delta = (touchStartY - currentY) * 0.002
  touchStartY = currentY

  if (isInNavScroll(e.target)) return

  if (scrollLocked.value) {
    forceTrackTop()
    e.preventDefault()
    applyProgress(progress.value + delta)
    return
  }

  if (delta < 0 && isAtTop()) {
    forceTrackTop()
    e.preventDefault()
    applyProgress(progress.value + delta)
  }
}

let resetRafId = 0
const cancelReset = () => {
  if (resetRafId) {
    cancelAnimationFrame(resetRafId)
    resetRafId = 0
  }
}

const resetProgressToZero = () => {
  cancelReset()
  if (progress.value <= 0) {
    applyProgress(0)
    return
  }

  const start = performance.now()
  const startValue = progress.value
  const duration = 700

  const tick = (now) => {
    const t = Math.min(1, (now - start) / duration)
    const eased = 1 - Math.pow(1 - t, 3)
    const next = startValue * (1 - eased)
    applyProgress(next)
    if (t < 1) {
      resetRafId = requestAnimationFrame(tick)
    }
  }

  resetRafId = requestAnimationFrame(tick)
}

onMounted(() => {
  setScrollLock(progress.value < 1)
  window.addEventListener('wheel', handleWheel, { passive: false })
  window.addEventListener('touchstart', handleTouchStart, { passive: true })
  window.addEventListener('touchmove', handleTouchMove, { passive: false })
  startAutoPlay()
})

onUnmounted(() => {
  window.removeEventListener('wheel', handleWheel)
  window.removeEventListener('touchstart', handleTouchStart)
  window.removeEventListener('touchmove', handleTouchMove)
  document.body.style.overflow = ''
  cancelReset()
  cancelAuto()
})
</script>

<style scoped>
.scroll-track {
  position: relative;
  height: 100vh;
  overflow: hidden;
}
</style>
